import { AnimationEvent } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  TemplateRef,
} from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { popoverAnimation } from './popover.animation';

/** Popover visibility type */
export type PopoverVisibility = 'opened' | 'closed';

/**
 * Popover component
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'atx-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
  animations: [popoverAnimation],
  standalone: false,
})
export class PopoverComponent implements OnDestroy {
  /** Popover title. */
  public title?: string | TemplateRef<unknown>;

  /** Popover content. */
  public content: string | TemplateRef<unknown>;

  private destroy$ = new Subject<void>();

  public contentChanged$ = new ReplaySubject<MutationRecord[]>(1);

  /** Popover visability state. */
  @HostBinding('@state')
  public state: PopoverVisibility = 'closed';

  /** Popover component host class. */
  @HostBinding('class')
  public popoverClass = 'popover';

  /** Close Subject. */
  private readonly onClose: Subject<void> = new Subject();

  /** Visability getter. */
  private get isVisible(): boolean {
    return this.state === 'opened';
  }

  /**
   * Animation finish handler.
   */
  @HostListener('@state.done', ['$event'])
  public doneListener(event: AnimationEvent): void {
    const toState = event.toState as PopoverVisibility;

    if (toState === 'closed' && !this.isVisible) {
      this.onClose.next();
    }
  }

  /**
   * Dependencies.
   */

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Popover close subject as observable.
   */
  public afterClosed(): Observable<void> {
    return this.onClose.asObservable().pipe(takeUntil(this.destroy$));
  }

  public afterContentChanged(): Observable<MutationRecord[]> {
    return this.contentChanged$.asObservable().pipe(takeUntil(this.destroy$));
  }

  /**
   * Check if passed item is TemplateRef.
   * @param item Item to check.
   */
  public isTemplate(item: string | TemplateRef<unknown>) {
    return item instanceof TemplateRef;
  }

  /**
   * Open popover. Initiates animation.
   */
  public open() {
    this.state = 'opened';

    this.cdr.markForCheck();
  }

  /**
   * Close popover. Initiates animation.
   */
  // tslint disabled because it says that this function is duplicate of 'open' function ¯\_(ツ)_/¯
  // tslint:disable-next-line
  public close() {
    this.state = 'closed';

    this.cdr.markForCheck();
  }

  public ngOnDestroy() {
    this.destroy$.next();
  }
}
